import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, InputAdornment, IconButton, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { getInboxListApi, initInboxThreadApi, deleteInboxThreadApi, getServerBaseUrl } from '../../api';
import ConfirmModal from '../../components/common/ConfirmModal';
import { ADMIN_NOTIFICATION_PREF_EVENT, ADMIN_NOTIFICATION_PREF_KEY } from '../../constants/notifications';

const NotificationSwitch = styled(Switch)({
  width: 42,
  height: 24,
  padding: 0,
  display: 'flex',
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: '#103c85',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#ffffff',
    width: 20,
    height: 20,
    borderRadius: '50%',
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    opacity: 1,
    backgroundColor: '#d1d5db',
  },
});

const InboxList = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [threads, setThreads] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThreadUserName, setSelectedThreadUserName] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const navigate = useNavigate();

  const currentUser = (() => {
    try {
      return JSON.parse(window.localStorage.getItem('currentUser') || 'null');
    } catch (e) {
      return null;
    }
  })();

  const isMember = currentUser?.role === 'MEMBER';
  const isAdmin = currentUser?.role === 'ADMIN';

  const getUnreadCount = (thread) => {
    const n = Number(thread?.unread_count ?? 0);
    if (Number.isFinite(n) && n > 0) return n;
    return thread?.is_unread ? 1 : 0;
  };

  const fetchThreads = useCallback(async () => {
    try {
      const res = await getInboxListApi({ keyword: searchTerm });
      const items = res?.data?.items || [];

      if (isMember) {
        const existingThreadId = items?.[0]?.thread_id;
        if (existingThreadId) {
          navigate(`/inbox/${existingThreadId}`);
          return;
        }

        const initRes = await initInboxThreadApi();
        const threadId = initRes?.data?.thread?.thread_id;
        if (threadId) {
          navigate(`/inbox/${threadId}`, {
            state: {
              thread: initRes?.data?.thread,
              messages: initRes?.data?.messages,
            },
          });
          return;
        }
      }

      setThreads(items);
    } catch (err) {
      console.error(err);
    }
  }, [searchTerm, isMember, navigate]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const loadPref = () => {
      try {
        const stored = window.localStorage.getItem(ADMIN_NOTIFICATION_PREF_KEY);
        setNotificationsEnabled(stored !== 'off');
      } catch (err) {
        setNotificationsEnabled(true);
      }
    };
    loadPref();

    const handleStorage = (event) => {
      if (event?.key === ADMIN_NOTIFICATION_PREF_KEY) {
        loadPref();
      }
    };

    const handlePrefEvent = (event) => {
      if (typeof event?.detail === 'boolean') {
        setNotificationsEnabled(event.detail);
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(ADMIN_NOTIFICATION_PREF_EVENT, handlePrefEvent);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ADMIN_NOTIFICATION_PREF_EVENT, handlePrefEvent);
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    // ✅ 공통 서버 베이스 URL 사용 (예: http(s)://도메인:4000)
    const SOCKET_URL = getServerBaseUrl();

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });

    if (currentUser.role === 'ADMIN') {
      socket.emit('join_room', 'admin_inbox');
    } else {
      socket.emit('join_room', `user_${currentUser.userId}`);
    }

    socket.on('inbox_updated', (data) => {
      setThreads((prev) => {
        const idx = prev.findIndex((t) => t.thread_id === data.thread_id);
        const isFromUserToAdmin =
          currentUser?.role === 'ADMIN' &&
          data?.sender_id &&
          data?.thread_user_id &&
          String(data.sender_id) === String(data.thread_user_id);

        if (idx > -1) {
          const prevUnread = getUnreadCount(prev[idx]);
          const nextUnread = isFromUserToAdmin ? prevUnread + 1 : prevUnread;
          const updated = {
            ...prev[idx],
            last_message_content: data.last_message_content,
            last_message_at: data.last_message_at,
            is_unread: isFromUserToAdmin ? 1 : prev[idx]?.is_unread,
            unread_count: nextUnread,
          };
          const newThreads = [...prev];
          newThreads.splice(idx, 1);
          return [updated, ...newThreads];
        }
        const nextUnread = isFromUserToAdmin ? 1 : 0;
        const newItem = {
          thread_id: data.thread_id,
          user_name: data.user_name || '알 수 없음',
          last_message_content: data.last_message_content,
          last_message_at: data.last_message_at,
          is_unread: nextUnread ? 1 : 0,
          unread_count: nextUnread,
        };
        return [newItem, ...prev];
      });
    });

    socket.on('thread_cleared', (data) => {
      const tid = data?.thread_id;
      if (!tid) return;
      setThreads((prev) => prev.filter((t) => String(t.thread_id) !== String(tid)));
    });

    return () => {
      socket.disconnect();
    };
  }, [currentUser]);

  const handleOpenDelete = (threadId, userName) => {
    setSelectedThreadId(threadId);
    setSelectedThreadUserName(userName || '');
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedThreadId) {
      setDeleteModalOpen(false);
      return;
    }
    try {
      await deleteInboxThreadApi(selectedThreadId);
      setThreads((prev) => prev.filter((t) => String(t.thread_id) !== String(selectedThreadId)));
      setDeleteModalOpen(false);
      setSelectedThreadId(null);
      setSelectedThreadUserName('');
    } catch (e) {
      console.error(e);
      if (e?.status === 401 || e?.suppressAlert) return;
      alert('대화방 삭제(나가기)에 실패했습니다.');
      setDeleteModalOpen(false);
      setSelectedThreadId(null);
      setSelectedThreadUserName('');
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedThreadId(null);
    setSelectedThreadUserName('');
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleToggleNotification = useCallback(
    (event) => {
      const next = Boolean(event.target.checked);
      setNotificationsEnabled(next);
      try {
        window.localStorage.setItem(ADMIN_NOTIFICATION_PREF_KEY, next ? 'on' : 'off');
        window.dispatchEvent(new CustomEvent(ADMIN_NOTIFICATION_PREF_EVENT, { detail: next }));
      } catch (err) {
        // ignore storage errors
      }
    },
    [],
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();

    if (isToday) {
      let hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? '오후' : '오전';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${ampm}${hours}:${minutes}`;
    } else {
      const year = d.getFullYear().toString().slice(2);
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${year}.${month}.${day}`;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          pb: 2,
          position: 'relative',
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />

        <Header variant="full" onClickMenu={() => setMenuOpen(true)} />

        <Box
          sx={{
            mb: 2,
            mt: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            rowGap: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 24 }}>
            쪽지함 리스트
          </Typography>
          {isAdmin && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 0.5 }}>
                알림
              </Typography>
              <NotificationSwitch
                checked={notificationsEnabled}
                onChange={handleToggleNotification}
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: notificationsEnabled ? '#103c85' : '#9ca3af',
                  minWidth: 32,
                  textAlign: 'center',
                }}
              >
                {notificationsEnabled ? 'ON' : 'OFF'}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2, mb: 1 }}>
          <TextField
            fullWidth
            placeholder="검색어를 입력해주세요."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#9ca3af', mr: 0.5 }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: 13 },
            }}
          />
        </Box>

        <Box>
          {threads.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <Typography color="textSecondary">리스트가 없습니다</Typography>
            </Box>
          ) : (
            threads.map((thread) => (
              <Box
                key={thread.thread_id}
                onClick={() => {
                  if (isAdmin) {
                    const curUnread = getUnreadCount(thread);
                    if (curUnread > 0) {
                      setThreads((prev) =>
                        prev.map((t) =>
                          String(t.thread_id) === String(thread.thread_id)
                            ? { ...t, is_unread: 0, unread_count: 0 }
                            : t
                        )
                      );
                    }
                  }
                  navigate(`/inbox/${thread.thread_id}`);
                }}
                sx={{
                  pl: 2,
                  pr: 2,
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  position: 'relative',
                }}
              >
                <Box sx={{ overflow: 'hidden', mr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {thread.user_name}
                    </Typography>
                    {isAdmin && getUnreadCount(thread) > 0 ? (
                      <Box
                        sx={{
                          minWidth: 18,
                          height: 18,
                          px: 0.75,
                          borderRadius: 999,
                          bgcolor: '#f87171',
                          color: 'white',
                          fontSize: 11,
                          fontWeight: 800,
                          display: 'grid',
                          placeItems: 'center',
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        {getUnreadCount(thread) > 99 ? '99+' : String(getUnreadCount(thread))}
                      </Box>
                    ) : null}
                  </Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    noWrap
                    sx={{ fontSize: '0.85rem', maxWidth: 230 }}
                  >
                    {thread.last_message_content || '대화 내용이 없습니다.'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    minWidth: 60,
                    textAlign: 'right',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    pr: 1,
                  }}
                >
                  {isAdmin ? (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDelete(thread.thread_id, thread.user_name);
                      }}
                      size="small"
                      sx={{ mb: 0.5 }}
                      aria-label="대화방 나가기"
                    >
                      <ExitToAppIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                    </IconButton>
                  ) : null}
                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                    {formatDate(thread.last_message_at)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
      <ConfirmModal
        open={deleteModalOpen}
        message={`${selectedThreadUserName ? `${selectedThreadUserName}님 ` : ''}대화방을 나가시겠습니까?`}
        confirmText="나가기"
        cancelText="취소"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </Box>
  );
};

export default InboxList;
